import { AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import { EntityProposal, indexEntityProposals } from "src/components/formFields/ai/utils/aiProposalForm";
import { parseAiNumber, parseAiString } from "src/components/formFields/ai/utils/bindAiProposal";
import { AiFieldBinding, aiFieldBinding } from "src/components/formFields/ai/utils/reconcilePendingAiProposals";
import { AiEntityProposalType, AiFieldProposalStatus, Maybe } from "src/generated/graphqlTypes";
import { PlanPackageLocationsV2_PlanPackageFragment } from "src/routes/libraries/plan-package-v2/components/LocationsTab.generated";
import { FieldState, ObjectState } from "src/utils/formState";
import { RoomFormValue, validateFullRoomNumber } from "./locationUtils";

/**
 * The `fieldName`s the tab understands.
 *
 * "roomNumber" | "roomTypeId" | "marketingName" are editable columns and map to `SaveReadyPlanLocationInput`. `active` is unique:
 * it is how a DELETE proposal states its intent (`active` -> `false`), has no editable field behind
 * it, and is resolved by removing the room rather than by writing a value.
 */
export type RoomProposalFieldName = "roomNumber" | "roomTypeId" | "marketingName" | "active";

/** The `fieldName`s that map to an editable column, i.e. everything except `active`. */
export const editableProposalFieldNames = ["roomNumber", "roomTypeId", "marketingName"] as const;

/**
 * Looks up the editable form state for one room's proposed field.
 *
 * Proposals live in a single tab-level `useFormState` rather than on each row's form state.
 */
export type FieldProposalLookup = (
  rplId: string,
  fieldName: RoomProposalFieldName,
) => ObjectState<AiFieldProposalFormValue> | undefined;

/** Every proposal for one room. */
export type RoomProposalsLookup = (rplId: string) => ObjectState<AiFieldProposalFormValue>[];

/**
 * Keys the plan package's entity proposals by the RPL they're about.
 *
 * Our import creates a CREATE proposal's RPL up-front, so every
 * proposal here — CREATE included — is keyed by a room that already exists in `roomLocations`.
 */
export function indexRoomProposals(
  planPackage: PlanPackageLocationsV2_PlanPackageFragment,
  includeProposals: boolean,
): Map<string, EntityProposal> {
  if (!includeProposals) return new Map();
  return indexEntityProposals(planPackage.aiProposal?.entityProposals, "ReadyPlanLocation");
}

/** Every proposal across the tab, flattened for the tab-level form's list field. */
export function flattenRoomProposals(roomProposals: Map<string, EntityProposal>): AiFieldProposalFormValue[] {
  return [...roomProposals.values()].flatMap((p) => p.fieldProposals);
}

/** The row's proposals paired with the form field each one writes to, for Accept All / Ignore All. */
export function roomProposalBindings(
  os: ObjectState<RoomFormValue>,
  rplId: string,
  levelName: string,
  getFieldProposal: FieldProposalLookup,
): AiFieldBinding[] {
  return [
    fullRoomNumberBinding(os, rplId, levelName, getFieldProposal),
    aiFieldBinding(os.roomTypeId as FieldState<Maybe<string>>, parseAiString, getFieldProposal(rplId, "roomTypeId")),
    aiFieldBinding(
      os.marketingName as FieldState<Maybe<string>>,
      parseAiString,
      getFieldProposal(rplId, "marketingName"),
    ),
  ];
}

/**
 * `roomNumber` proposals carry the full number the AI read off the drawing (194), but the field
 * stores the two-digit part (94), so the parse runs it through the level's range check first.
 *
 * An out-of-range number parses to `undefined`, which leaves that proposal pending even under Accept
 * All — the row keeps its warning rather than silently accepting a number we know is wrong.
 */
export function fullRoomNumberBinding(
  os: ObjectState<RoomFormValue>,
  rplId: string,
  levelName: string,
  getFieldProposal: FieldProposalLookup,
): AiFieldBinding {
  return aiFieldBinding(
    os.roomNumber as FieldState<Maybe<number>>,
    (raw) => {
      const full = parseAiNumber(raw);
      if (full === undefined) return undefined;
      const validated = validateFullRoomNumber(levelName, full);
      return "error" in validated ? undefined : validated.roomNumber;
    },
    getFieldProposal(rplId, "roomNumber"),
  );
}

/**
 * Whether resolving this room's proposals means deleting it.
 *
 * Gated on *every* proposal rather than *some*, so editing one field on a proposed row doesn't
 * delete it — only an explicit whole-row Ignore (on a CREATE) or Accept (on a DELETE) does.
 */
export function roomNeedsDelete(proposal: EntityProposal | undefined, statuses: AiFieldProposalStatus[]): boolean {
  if (!proposal || statuses.isEmpty) return false;
  const every = (status: AiFieldProposalStatus) => statuses.every((s) => s === status);
  return (
    (proposal.type === AiEntityProposalType.Create && every(AiFieldProposalStatus.Rejected)) ||
    (proposal.type === AiEntityProposalType.Delete && every(AiFieldProposalStatus.Accepted))
  );
}

export type RoomWarning = { rplId: string; message: string };

export type RoomSummary = { rplId: string; levelName: string; fullRoomNumber: number | null | undefined };

export type ProposedRoomNumber = { status: AiFieldProposalStatus | undefined; fieldValue: string | undefined };

/**
 * The warnings shown beside proposed rows.
 *
 * Takes a lookup rather than the raw server data so a warning clears the moment its proposal is
 * resolved — by Accept All, Ignore All, or the user simply fixing the number. Callers read the live
 * form state inside the lookup, which keeps this function pure and testable.
 *
 * There is no warning column on `AiFieldProposal`, so these are computed the same way the Order cell
 * validates a number the user types.
 */
export function deriveRoomWarnings(
  rooms: RoomSummary[],
  getProposedRoomNumber: (rplId: string) => ProposedRoomNumber | undefined,
): RoomWarning[] {
  return rooms.flatMap(({ rplId, levelName }) => {
    const proposed = getProposedRoomNumber(rplId);
    // A resolved proposal has nothing left to warn about.
    if (proposed?.status !== AiFieldProposalStatus.Pending) return [];
    const full = parseAiNumber(proposed.fieldValue);
    if (full === undefined) {
      return [{ rplId, message: "Missing room number — extracted without a number. Assign one to continue." }];
    }
    const validated = validateFullRoomNumber(levelName, full);
    if ("error" in validated) {
      return [{ rplId, message: `Unexpected numbering — ${validated.error}. "${full}" doesn't match the convention.` }];
    }
    if (rooms.some((other) => other.rplId !== rplId && other.fullRoomNumber === full)) {
      return [{ rplId, message: `Duplicate room number — ${full} is already used on this plan.` }];
    }
    return [];
  });
}
